import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import EntryTypeBadge from './EntryTypeBadge'
import DimensionTag from './DimensionTag'

// One card in the catalog browse grid. Matches the aesthetic of the
// existing ArtifactCard and InterviewSession list cards.
//
// Shows up to 4 dimension tags by picking one value from each of the first
// 4 populated dimensions — this surfaces a variety of facets (not just
// four tools).
const MAX_TAGS = 4

export default function CatalogEntryCard({ entry }) {
  // Called unconditionally (hooks rule) before the early return below.
  const location = useLocation()

  if (!entry) return null

  const firstPerson = (entry.people || [])[0]
  const sourceCount = entry.source_count ?? 0

  // Pick one value from each populated dimension, up to MAX_TAGS.
  const dimensionPairs = []
  if (entry.dimensions) {
    for (const [ slug, values ] of Object.entries(entry.dimensions)) {
      if (dimensionPairs.length >= MAX_TAGS) break
      if (Array.isArray(values) && values.length > 0) {
        dimensionPairs.push([ slug, values[0] ])
      }
    }
  }

  return (
    <article className="group flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-accent-300 hover:shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <Link
          to={`/catalog/${entry.id}`}
          // The detail page's back link reads location.state.from to return
          // the user to the same filtered browse view (filters live in the
          // URL search params).
          state={{ from: `${location.pathname}${location.search}` }}
          className="text-base font-semibold text-slate-900 hover:text-accent-600 leading-snug"
        >
          {entry.title}
        </Link>
        <EntryTypeBadge entryType={entry.entry_type} className="shrink-0" />
      </div>

      {entry.summary && (
        <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3">
          {entry.summary}
        </p>
      )}

      {dimensionPairs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {dimensionPairs.map(([ slug, value ]) => (
            <DimensionTag
              key={`${slug}-${value.id}`}
              dimensionSlug={slug}
              value={value}
              dense
            />
          ))}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-2 min-w-0">
          {firstPerson ? (
            <>
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[11px] font-medium text-slate-600 shrink-0">
                {(firstPerson.name || '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="truncate">
                {firstPerson.name}
                {firstPerson.company ? <span className="text-slate-400"> · {firstPerson.company}</span> : null}
              </span>
            </>
          ) : (
            <span className="text-slate-400">No attribution</span>
          )}
        </div>
        <span className="shrink-0">
          {sourceCount} source{sourceCount === 1 ? '' : 's'}
        </span>
      </div>
    </article>
  )
}
